/** Asset management — module 9. */

export type AssetStatus = 'IN_STOCK' | 'ASSIGNED' | 'IN_MAINTENANCE' | 'LOST' | 'DISPOSED'
export type AssetCondition = 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED'
export type AssignmentStatus = 'ASSIGNED' | 'RETURNED' | 'LOST'
export type DepreciationMethod = 'STRAIGHT_LINE' | 'REDUCING_BALANCE'
export type LicenceEnforcement = 'BLOCK' | 'WARN'
export type MaintenanceKind = 'SERVICE' | 'REPAIR' | 'INSPECTION' | 'CALIBRATION'
export type DisposalMethod =
  | 'SOLD' | 'SCRAPPED' | 'DONATED' | 'RETURNED_TO_VENDOR' | 'WRITTEN_OFF'
export type StocktakeStatus = 'OPEN' | 'CLOSED' | 'CANCELLED'
export type CountOutcome = 'NOT_COUNTED' | 'FOUND' | 'MISSING' | 'FOUND_ELSEWHERE' | 'DAMAGED'

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  IN_STOCK: 'In stock',
  ASSIGNED: 'With somebody',
  IN_MAINTENANCE: 'In maintenance',
  LOST: 'Lost',
  DISPOSED: 'Disposed of',
}

export const CONDITION_LABELS: Record<AssetCondition, string> = {
  NEW: 'New',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  DAMAGED: 'Damaged',
}

export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
  ASSIGNED: 'Out',
  RETURNED: 'Returned',
  LOST: 'Lost',
}

export const DEPRECIATION_LABELS: Record<DepreciationMethod, string> = {
  STRAIGHT_LINE: 'Straight line',
  REDUCING_BALANCE: 'Reducing balance',
}

export const MAINTENANCE_KIND_LABELS: Record<MaintenanceKind, string> = {
  SERVICE: 'Service',
  REPAIR: 'Repair',
  INSPECTION: 'Inspection',
  CALIBRATION: 'Calibration',
}

export const DISPOSAL_METHOD_LABELS: Record<DisposalMethod, string> = {
  SOLD: 'Sold',
  SCRAPPED: 'Scrapped',
  DONATED: 'Donated',
  RETURNED_TO_VENDOR: 'Returned to the vendor',
  WRITTEN_OFF: 'Written off',
}

export const COUNT_OUTCOME_LABELS: Record<CountOutcome, string> = {
  NOT_COUNTED: 'Not counted',
  FOUND: 'Found',
  MISSING: 'Missing',
  FOUND_ELSEWHERE: 'Found somewhere else',
  DAMAGED: 'Damaged',
}

export interface AssetCategoryView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  /** Null means this category has no book value at all. */
  depreciationMethod: string | null
  usefulLifeMonths: number | null
  residualPercent: number | null
  requiredDocumentTypeId: string | null
  requiredDocumentTypeName: string | null
  licenceEnforcement: string | null
  trackSerial: boolean
  active: boolean
  assetCount: number
  depreciationNote: string
}

export interface AssetView {
  id: string
  legalEntityId: string | null
  categoryId: string
  categoryName: string | null
  assetTag: string
  name: string
  description: string | null
  serialNumber: string | null
  model: string | null
  manufacturer: string | null
  purchaseDate: string | null
  purchaseCost: number | null
  currency: string | null
  supplier: string | null
  invoiceFileId: string | null
  warrantyExpiry: string | null
  recoveryValue: number | null
  status: string
  condition: string
  locationId: string | null
  costCenterId: string | null
  notes: string | null
  customFields: Record<string, unknown>
  heldByEmployeeId: string | null
  heldByName: string | null
  heldSince: string | null
  /** Null when the category has no depreciation method. */
  bookValue: number | null
  valuationNote: string | null
}

export interface AssetAssignmentView {
  id: string
  assetId: string
  assetTag: string | null
  assetName: string | null
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  assignedOn: string
  expectedReturnOn: string | null
  conditionOut: string
  purpose: string | null
  returnedOn: string | null
  conditionIn: string | null
  returnNote: string | null
  status: string
  licenceNote: string | null
  overdue: boolean
}

export interface LicenceCheckView {
  required: boolean
  satisfied: boolean
  documentTypeName: string | null
  enforcement: string | null
  note: string
}

export interface AssetMaintenanceView {
  id: string
  assetId: string
  assetTag: string | null
  kind: string
  scheduledOn: string | null
  performedOn: string | null
  nextDueOn: string | null
  vendor: string | null
  cost: number | null
  currency: string | null
  description: string | null
  tookOutOfService: boolean
  done: boolean
  overdue: boolean
}

export interface AssetDisposalView {
  id: string
  assetId: string
  assetTag: string | null
  assetName: string | null
  disposedOn: string
  method: string
  proceeds: number | null
  currency: string | null
  bookValue: number | null
  gainLoss: number | null
  valuationNote: string | null
  reason: string | null
  approvedBy: string | null
}

export interface AssetStocktakeView {
  id: string
  legalEntityId: string | null
  reference: string
  name: string
  categoryId: string | null
  categoryName: string | null
  locationId: string | null
  startedOn: string
  closedOn: string | null
  status: string
  notes: string | null
  inScope: number
  counted: number
  notCounted: number
  found: number
  missing: number
  foundElsewhere: number
  damaged: number
  varianceNote: string
}

export interface AssetStocktakeLineView {
  id: string
  stocktakeId: string
  assetId: string
  assetTag: string | null
  assetName: string | null
  expectedStatus: string
  expectedHolderId: string | null
  expectedHolderName: string | null
  expectedLocationId: string | null
  outcome: string
  foundLocationId: string | null
  foundHolderId: string | null
  foundHolderName: string | null
  foundCondition: string | null
  countedAt: string | null
  note: string | null
  variance: boolean
}

export interface UnreturnedAssetView {
  assetId: string
  assetTag: string
  name: string
  heldSince: string
  status: string
  /** Null when this one could not be valued; the note says why. */
  amount: number | null
  note: string
}

export interface AssetRecoveryView {
  assets: UnreturnedAssetView[]
  total: number | null
  valuedCount: number
  unvaluedCount: number
  valuation: string | null
  basis: string
}

export interface AssetSummaryView {
  total: number
  inStock: number
  assigned: number
  inMaintenance: number
  lost: number
  disposed: number
  purchaseCost: number | null
  bookValue: number | null
  currency: string | null
  unvalued: number
  valuationNote: string
}
